import { navigate } from 'gatsby';
import { LicenseType, User } from 'services/interface';
import { DashboardTabs } from 'services/tabs';

export const USER_ROLES_ACCESS_DENIED = {
    admin: [],
    /** Not in an organization + API org_role null (MDCL-16412). Commercial shell users are paid; SPS matches this role, not license flags. */
    'non-org-user': [],
    user: ['notifications', 'workflows', 'engines', 'secure-pipeline-studio'],
    analyst: [
        'analyze',
        'security',
        'notifications',
        'workflows',
        'engines',
        'secure-pipeline-studio',
        'account/api-key',
    ],
} as const;

export type UserRole = keyof typeof USER_ROLES_ACCESS_DENIED;

const canAccessCswg = (user: User | null | undefined): boolean => {
    return Boolean(user?.isCswgTenantAdmin);
};

export const isInOrganization = (user: User | null | undefined): boolean => {
    return Boolean(user?.organization?.organization_id);
};

export const isPaidUser = (user: User | null | undefined): boolean => {
    return user?.licenseInfo?.paidUser === 1 || user?.paid_user === 1;
};

/** The AI Content Inspection surfaces render for every tier — see `canOpenAiContentInspector`. */
export const canSeeAiContentInspector = (_user: User | null | undefined): boolean => {
    return true;
};

/**
 * ...but only anonymous visitors and free accounts can open them: the tab is listed for a paid plan
 * and disabled there, so the surface stays visible without leading anywhere.
 */
export const canOpenAiContentInspector = (user: User | null | undefined): boolean => {
    return !isPaidUser(user);
};

const getUserRole = (user: User | null | undefined): UserRole => {
    if (user == null) {
        return 'user';
    }

    if (!isInOrganization(user) && user.org_role === null) {
        return 'non-org-user';
    }

    if (!user.org_role) {
        return 'user';
    }

    const role = user.org_role;

    if (!(role in USER_ROLES_ACCESS_DENIED)) {
        return 'user';
    }

    return role;
};

const canAccessSecurePipelineStudio = (user: User | null | undefined): boolean => {
    if (isInOrganization(user)) {
        return user?.org_role === 'admin' || user?.org_role === 'user' || user?.org_role === 'analyst';
    }
    return getUserRole(user) === 'non-org-user';
};

export const canEditSecurePipelineStudio = (user: User | null | undefined): boolean => {
    if (isInOrganization(user)) {
        return user?.org_role === 'admin';
    }
    return getUserRole(user) === 'non-org-user';
};

/**
 * IP Allowlist is an organization-level setting backed by an admin-only endpoint
 * (get-ips / update-ips). Non-admin org roles (e.g. 'user') cannot retrieve the
 * ranges, so the section must be hidden for them rather than showing a fetch error
 * (MDCL-17095).
 */
export const canManageIpAllowlist = (user: User | null | undefined): boolean => {
    return isInOrganization(user) && user?.org_role === 'admin';
};

export const canAccessHomePage = (user: User | null | undefined): boolean => {
    return !(isInOrganization(user) && user?.org_role === 'analyst');
};

/**
 * Deliberate home navigation (logo / home buttons). The in-memory flag tells WrapPage the
 * arrival at `/` is an explicit visit, so the authenticated-landing redirect (MDCL-18276)
 * must not bounce the user back to their license default. Deliberately NOT history state:
 * that persists on the history entry (surviving login on the same page), which would
 * suppress the post-login dashboard redirect. In-memory intent dies on any full page load,
 * so the login flow always lands on the license default.
 */
let deliberateHomeNav = false;

export const isDeliberateHomeNav = (): boolean => deliberateHomeNav;

export const clearDeliberateHomeNav = (): void => {
    deliberateHomeNav = false;
};

export const navigateToHomePage = (user: User | null | undefined): void => {
    if (canAccessHomePage(user)) {
        // Only a logged-in user needs the landing-redirect exemption; guests aren't redirected.
        deliberateHomeNav = user?.isLoggedIn === true;
        void navigate('/');
    } else {
        void navigate(`/dashboard/${DashboardTabs.Security}`);
    }
};

/**
 * Post-auth landing default (MDCL-18276): any account above free Community — a paid license
 * tier (commercial/standard/professional/internal/enterprise), a paid user, or any
 * organization member (user/admin) — lands on the dashboard; only free community accounts
 * (including accounts with no license info) land on Analyze. Roles denied the target fall
 * back to the dashboard instead of an access-denied page.
 */
export const getLicenseDefaultPath = (user: User | null | undefined): string => {
    const licenseType = user?.organization?.mdc_license_type ?? user?.mdc_license_type ?? user?.licenseInfo?.licenseType;
    const isEnterprise = licenseType === LicenseType.Enterprise || user?.is_enterprise === true;
    // Free tiers are enumerated; every other known license tier is paid (MDCL-18276 review).
    const isFreeTier = licenseType == null || licenseType === LicenseType.Community;
    const isCommercial = isEnterprise || !isFreeTier || isPaidUser(user) || isInOrganization(user);
    const target = isCommercial ? '/dashboard' : '/analyze';
    return canAccessPath(user, target) ? target : '/dashboard';
};

export const canAccessReports = (user: User | null | undefined): boolean => {
    if (user?.isLoggedIn !== true) {
        return false;
    }
    return isPaidUser(user) || isInOrganization(user);
};

/**
 * Blog management/editor access is restricted in production to the internal
 * admin dashboard organization only.
 */
export const canManageBlogs = (user: User | null | undefined): boolean => {
    if (process.env.GATSBY_ENV !== 'prod') {
        return true;
    }

    return user?.organization?.organization_id === 'a9db434b66d239be5d2567bfb188bb24';
};

/** Pages that require an authenticated session, regardless of role. */
const LOGIN_REQUIRED_PAGES: readonly string[] = [
    'dashboard',
    'reports',
    'analyze',
    'account/api-key',
    'secure-pipeline-studio',
    'workflows',
    'engines',
    'profile',
    'settings',
];

export const canAccessPage = (user: User | null | undefined, page: string): boolean => {
    if (LOGIN_REQUIRED_PAGES.includes(page) && user?.isLoggedIn !== true) {
        return false;
    }

    if (page === 'account/api-key') {
        return !(USER_ROLES_ACCESS_DENIED[getUserRole(user)] as readonly string[]).includes('account/api-key');
    }

    if (page === 'store') {
        return Boolean(user?.isOnlineStore);
    }

    if (page === 'cswg') {
        return canAccessCswg(user);
    }

    if (page === 'workflows') {
        return canAccessSecurePipelineStudio(user);
    }

    if (page === 'engines') {
        return canAccessSecurePipelineStudio(user);
    }

    if (page === 'secure-pipeline-studio') {
        return canAccessSecurePipelineStudio(user);
    }

    if (page === 'notifications' && !isInOrganization(user)) {
        return false;
    }

    if (page === 'security' && !isInOrganization(user) && !isPaidUser(user)) {
        return false;
    }

    const role = getUserRole(user);
    const deniedPages = USER_ROLES_ACCESS_DENIED[role] as readonly string[];

    return !deniedPages.includes(page);
};

export const canAccessPath = (user: User | null | undefined, pathname: string): boolean => {
    if (!pathname) return true;

    const pathParts = pathname.split('/').filter(Boolean);

    if (pathParts.length === 0) return true; // Home page

    const [section, tab] = pathParts;

    // Dashboard tabs: check 'dashboard' access only (not individual tabs)
    if (section === 'dashboard') {
        return canAccessPage(user, 'dashboard');
    }

    // Settings tabs: check specific tab access
    if (section === 'settings' && tab) {
        return canAccessPage(user, tab);
    }

    // Secure Pipeline Studio: check workflows and engines access
    if (section === 'secure-pipeline-studio') {
        // Check if user has access to secure-pipeline-studio
        if (!canAccessPage(user, 'secure-pipeline-studio')) {
            return false;
        }
        // If there's a sub-path (workflows or engines), check that too
        if (tab) {
            return canAccessPage(user, tab);
        }
        return canAccessPage(user, 'secure-pipeline-studio');
    }

    // CSWG: always enforce access for the entire section.
    if (section === 'cswg') {
        return canAccessPage(user, 'cswg');
    }

    // Dedicated documentation route — requires an authenticated session.
    if (section === 'account' && tab === 'api-key') {
        return canAccessPage(user, 'account/api-key');
    }

    // Top-level pages: check page name
    if (pathParts.length === 1) {
        return canAccessPage(user, section);
    }

    return true;
};
